import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router';
import { useSession } from '../../state/useSession';
import { useBuildRun } from '../../state/buildRun';
import { takeAutoBuild } from '../../state/autoBuild';
import ExitQuestion, { exitQuestionAsked } from './ExitQuestion';
import { useDraft } from '../../state/draft';
import { clearDraftStash, restoreDraft, stashDraft, type StashResult } from '../../state/draftStash';
import { rememberBuildAfterSignUp, takeBuildAfterSignUp } from '../../state/anonymous';
import { readIntent } from '../../state/buildIntent';
import { resumableIntent } from '@/store/buildIntent';
import {
  buildsIncluded,
  checkoutOffered,
  offerFor,
  offerTerms,
  rememberCheckoutOffer,
  reportInitiateCheckout,
  trialCheckoutAwaited,
  TRIAL_DAYS,
  type Offer,
} from '../../state/trial';
import { ApiError } from '../../api/client';
import { tailorPlan } from '../../state/tailor';
import { noteWaiting } from '../../api/build';
import { creditBalance, startCheckout, type CreditBalance } from '../../api/billing';
import { leaveFor } from '../../platform/leave';
import { scaffoldFor } from '@/webapp/plan/scaffolds';
import { isCollecting, userFeature, type AppPlan } from '@/webapp/plan/planTypes';
import { KITS, kitById } from '@/webapp/runtime/kits';
import { kitOptions, withTopPick } from '@/webapp/runtime/kitOptions';
import { buildCostNote } from '@/billing/buildCostNote';
import { rankKitsFromFeedback } from '@/onboarding/rankKitsFromFeedback';
import KitMockup from '../../components/KitMockup';
import { pickStyleFromPicture } from '@/onboarding/pickStyleFromPicture';
import { generate } from '../../api/llm';
import { ANYTHING_ELSE_ID, withAnythingElse } from '@/components/wizard/layoutChoice';
import { documentReady } from '@/onboarding/pickContent';
import { readPicks } from '../project/attachments';
import ContentStep from './ContentStep';
import AttachmentsReview from './AttachmentsReview';
import JourneyDiagram from './JourneyDiagram';
import Paywall from './Paywall';
import { phaseLabel } from '../../shell/BuildOverlay';
import { funnelStep, useFunnelStep, type FunnelStep, type FunnelProps } from '../../analytics/funnel';
import { secondsBucket } from '../../analytics/visits';
import { Button, Screen } from '../../ui';

type StepId = 'features' | 'content' | 'look' | 'charts' | 'review';

const TITLES: Record<StepId, string> = {
  features: 'What it does',
  content: 'Your own content',
  look: 'How it looks',
  charts: 'What it shows',
  review: 'Ready to build',
};

const SUBTITLES: Record<StepId, string> = {
  features: 'Tap to include or leave out. Add anything missing.',
  // The step's own lead says what goes here; this only says it can be skipped (it said "Optional." twice).
  content: 'Optional — skip this if you have nothing to add.',
  look: 'Three directions for your app. Tap the one you want.',
  charts: 'Built from your own data, never invented numbers.',
  review: 'Change anything here — nothing is built until you tap Build.',
};

/** What a signed-out visitor is told on the look step — where "Try again" would never work. */
const SIGN_IN_FOR_LOOKS = 'Describing a look, or matching one to a picture, needs a free account. Or pick one of these.';

/** What somebody signed in is told when /llm refuses them: they HAVE an account; it is the session that ended. */
const SESSION_ENDED = 'Your session has ended. Sign in again to describe a look or match a picture — or pick one of these.';

/**
 * Whether the look step's two model calls, "Match a picture" and "Warmer? Bolder?", are this
 * account's to use (F14, 2026-09-30).
 *
 * /llm serves them free, but only to an account with a balance behind it (infra/lambda/handlers/
 * llm.ts: "a free operation still needs a balance"), and since the free tier went a new account has
 * none. An ad visitor who had just signed up was offered both, picked a photo, and was told "This
 * account does not have the credits to match a picture": credits they never had.
 *
 * Hidden, not made free: free needs a server change and a per-account cap on a vision call anyone
 * can reach by signing up, while hiding keeps every step of a new visitor's path working (the three
 * looks, "Show me others" and Build are all there) and the tools arrive with the trial they are
 * about to be offered at Build. Mirrors the server's test: unlimited, or any credits. 'unknown'
 * until the balance is read, and a read that fails leaves them offered: the server is the gate,
 * and its refusal is now said as what it is (lookCall).
 */
type LookTools = 'unknown' | 'open' | 'plan' | 'topup';

function lookToolsFor(balance: { tier?: string; creditsRemaining?: number; subscribed?: boolean }): LookTools {
  if (balance.tier === 'unlimited') return 'open';
  if (typeof balance.creditsRemaining === 'number' && balance.creditsRemaining > 0) return 'open';
  // A subscriber whose month has run out: more credits, not a plan they already have.
  return balance.subscribed === true || balance.tier === 'paid' ? 'topup' : 'plan';
}

/** Said where the look tools would be, to an account they are not open to. */
function lookToolsNeed(tools: 'plan' | 'topup', offer: Offer): string {
  if (tools === 'topup') {
    return "This month's credits are used up, so matching a picture or describing a look is back when they refill. Pick one of these, or Show me others.";
  }
  const what = offer === 'subscribe' ? 'a subscription' : `the ${TRIAL_DAYS}-day free trial`;
  return `Matching a picture and describing a look come with ${what}, offered when you build. For now, pick one of these, or Show me others.`;
}

/** Build could not find out whether this account has a plan — a fact about the moment, so no guess. */
const PLAN_CHECK_FAILED = "We couldn't check your plan just now, so nothing was started. Check your connection and press it again.";

/**
 * Said on the review when this tab's checkout is Stripe's and the account has not caught up yet —
 * named for what that checkout sold (state/trial.ts `checkoutOffered`).
 */
const awaitedHeadline = (offer: Offer): string => `Stripe has your ${offer === 'subscribe' ? 'subscription' : 'trial'}.`;
const TRIAL_AWAITED_DETAIL =
  "Your account is still catching up with it, so Build waits for that and then builds this plan. There's no second checkout.";

/**
 * /plan refused for want of a subscription: the plan on screen is the untailored one, and why. What
 * it needs is what the checkout would sell this account — a trial it has had is not on offer.
 */
function tailoringNeeds(offer: Offer): string {
  const what = offer === 'subscribe' ? 'a subscription' : `the ${TRIAL_DAYS}-day free trial`;
  return `A tailored plan needs ${what}, so this one is made from your description alone. It builds just the same.`;
}

/**
 * Said on the plan Stripe's Cancel sends somebody back to. The week and the card only for a
 * checkout that was the trial: to an account that has had it they are not on offer.
 */
function checkoutCancelled(offer: Offer | null): string {
  return offer === 'subscribe'
    ? 'Checkout was cancelled, so nothing was charged.'
    : `Checkout was cancelled, so nothing was charged. The ${TRIAL_DAYS}-day free trial needs a card up front, and nothing is charged for ${TRIAL_DAYS} days.`;
}

/*
 * How long the page stays after InitiateCheckout has been handed to the pixel. fbevents sends it
 * as a request of its own, and a navigation started in the same moment can cancel a request
 * still in flight; this is the event the ads optimize for, and Stripe's page takes longer than
 * this to appear anyway.
 */
const LEAVE_AFTER_EVENT_MS = 300;

/** "a.jpg", "a.jpg and b.mp3", "a.jpg, b.mp3 and c.txt". */
function listed(names: string[]): string {
  return names.length <= 1 ? names.join('') : `${names.slice(0, -1).join(', ')} and ${names[names.length - 1]}`;
}

/** What could not be carried through checkout, by name, and what to do about it. */
export function notKept(names: string[]): string {
  const them = names.length === 1 ? 'it' : 'them';
  return `${listed(names)} couldn't be kept through checkout in this browser — attach ${them} again on the “Your own content” step.`;
}

// No 'charts' step: which numbers are worth drawing is a judgement the planner already
// makes, and it is not a decision worth stopping somebody for before they have seen the
// app. They are still planned, still built, still listed on the review step.
const STEPS: StepId[] = ['features', 'content', 'look', 'review'];

/*
 * How long the price waits on /plan before reading the balance anyway.
 *
 * It waits at all because /plan takes its charge BEFORE the model runs, and a balance read
 * sent alongside could come back first. But the charge is taken in the call's first moments;
 * the model is the slow part, and the client lets a plan run for the build Lambda's full ten
 * minutes — so waiting for the answer let a hung call keep the price off the screen for all
 * of that. Past this the charge is already in the balance, and the balance is read again when
 * /plan does settle, since a refund raises it.
 */
const PLAN_CHARGE_TAKEN_MS = 15_000;
/** The longest the first step's shortcut waits for /plan's tailoring (see tailorGaveUp). */
const SHORTCUT_WAITS_MS = 60_000;


/** A picture, read by the browser, as the base64 data URI the vision call takes. */
function readAsDataUri(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => reject(new Error('could not read that picture'));
    reader.onload = () => resolve(String(reader.result ?? ''));
    reader.readAsDataURL(file);
  });
}

/**
 * /new/plan, and /new/plan?checkout=cancelled — where Stripe's Cancel sends somebody who left
 * the trial checkout without paying.
 *
 * That return is a NEW page: the draft in memory went when the browser left for Stripe. Build
 * put it away first (state/draftStash.ts), so it is brought back here BEFORE the steps mount —
 * they read the draft once, as they open — and they open where Build was pressed, with the plan
 * as it was and a short line about the card and the 7 days.
 */
/**
 * The plan with no "Anything else" words on it, when the box is blank.
 *
 * On the web the box lives in the draft, beside the plan, so a blank box is the person's answer —
 * not the phone's case of a box emptied by a remount. Review nit (2026-09-30): `withAnythingElse`
 * treats blank as "change nothing", and the plan comes back from the store after a trip to Stripe
 * or sign-in already carrying the words from the last Build; clearing the box and pressing Build
 * again built the deleted words anyway, and they stayed on the features step. `layout.tweak` is
 * the words' other home (the web has no other way to write one).
 */
function withoutAnythingElse(plan: AppPlan, words: string): AppPlan {
  if (words.trim()) return plan;
  const features = plan.features.filter((f) => f.id !== ANYTHING_ELSE_ID);
  let layout = plan.layout;
  if (layout?.tweak !== undefined) {
    layout = { ...layout };
    delete layout.tweak;
  }
  return features.length === plan.features.length && layout === plan.layout ? plan : { ...plan, features, layout };
}

/**
 * The plan of the build this browser left held for the trial, back in the draft from its record
 * (state/buildIntent.ts), on the review step — and Build pressed once for them when the plan opens
 * (`pressBuildOnArrival`), which asks the server under the same key: the held app comes back at
 * once and the offer opens on it. False when there is no such build for this account here.
 */
let pressBuildOnArrival = false;
function restoreHeldDraft(ownerId: string): boolean {
  const intent = readIntent();
  /*
   * Held for the trial, or a build that failed or was left mid-way (2026-10-07: a golf tracker
   * failed behind the offer while its maker was gone) — any build of theirs this browser still
   * has on record comes back, and the press it gets asks the server under the same key.
   */
  if (!intent || intent.ownerId !== ownerId || !resumableIntent(intent)) return false;
  useDraft.setState({
    description: intent.goal,
    appName: intent.appName,
    plan: intent.plan ?? null,
    vibe: intent.vibe ?? null,
    styleBrief: intent.styleBrief ?? null,
    kind: intent.kind ?? 'personal',
    planStep: 'review',
  });
  pressBuildOnArrival = true;
  return true;
}

export default function Plan() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const { user, isInitializing } = useSession();
  const cancelled = params.get('checkout') === 'cancelled';
  /*
   * And a page that arrives with NO draft: the browser's own Back on Stripe's page returns to
   * plain /new/plan, which out of the back-forward cache is a fresh load with memory empty. With
   * a plan put away in this tab, that is the plan they left; with none, this opens as it always
   * has. Read once, on arrival — a draft emptied later is somebody's own doing.
   */
  const [arrivedEmpty] = useState(() => !useDraft.getState().description.trim());
  const restore = cancelled || arrivedEmpty;
  // Stripe's Cancel sends them here: a checkout that came back without a card (analytics/funnel.ts).
  useFunnelStep('checkout_returned', cancelled, { outcome: 'cancelled' });
  const [back, setBack] = useState<'restoring' | 'missing' | { lost: string[] } | null>(restore ? 'restoring' : null);
  /*
   * The "Your app is still waiting" email's button (infra/lambda/shared/waitingEmail.ts). Counted
   * once, and the plan it is about brought back from the build this browser left held for the
   * trial when nothing was put away (the stash is for a trip to Stripe, and lasts a day).
   */
  const fromEmail = params.get('from') === 'waiting_email';
  useFunnelStep('waiting_email_clicked', fromEmail);

  useEffect(() => {
    // Signed out by the time the email is opened: signed in first, then back here.
    if (!fromEmail || isInitializing || user) return;
    navigate('/sign-in', { replace: true, state: { from: '/new/plan?from=waiting_email' } });
  }, [fromEmail, isInitializing, user, navigate]);

  useEffect(() => {
    /*
     * Waits for the session, then restores for whoever it is — signed out too, since the journey
     * is open until Build (2026-10-03). A plan stashed signed out is claimed by the account that
     * signs in next in this tab (draftStash.ts): the email code is the step where a phone browser
     * most often reloads the page.
     */
    if (!restore || isInitializing) return;
    let live = true;
    if (fromEmail && !user) return;
    void restoreDraft(user?.id ?? '').then((restored) => {
      if (!live) return;
      if (!restored && fromEmail && user && restoreHeldDraft(user.id)) {
        setBack({ lost: [] });
        return;
      }
      setBack(restored ?? 'missing');
    });
    return () => {
      live = false;
    };
  }, [restore, isInitializing, user?.id, fromEmail]);

  if (back === 'restoring') {
    return (
      <Screen title={TITLES.review}>
        <p role="status" className="type-body plan-sub">Bringing back your plan…</p>
      </Screen>
    );
  }
  /*
   * Nothing put away for this account in this tab — another tab, a day later, or site data
   * cleared at Stripe. Still told that nothing was charged, and sent back to the idea rather than
   * shown a plan for an empty one.
   *
   * And whether or not a checkout was left (F19, 2026-09-30): the draft lives in memory, so a plain
   * reload here with nothing put away drew a plan for an EMPTY idea — "My app", generic features —
   * whose Build failed "Describe the app you want first." on every press.
   */
  if (back === 'missing' && !useDraft.getState().description.trim()) {
    return (
      <Screen title="Your plan">
        {cancelled && <p role="status" className="type-body plan-sub">{checkoutCancelled(checkoutOffered())}</p>}
        <p className="type-body plan-detail">
          {cancelled
            ? 'We couldn’t find the plan you were making in this tab, so it needs describing again.'
            : 'There’s no plan open in this tab — a reload starts it afresh. Describe your app and we’ll plan it again.'}
        </p>
        <Button title="Start from your idea" onPress={() => navigate('/new/idea')} />
      </Screen>
    );
  }
  return <PlanSteps cancelled={cancelled ? { lost: typeof back === 'object' && back ? back.lost : [] } : null} />;
}

/**
 * Ported from src/app/(onboarding)/plan.tsx.
 *
 * The plan on screen is a SCAFFOLD rendered synchronously from the idea, so this paints
 * immediately and never shows a spinner over an empty page. The model pass then refines
 * it in place. If that pass fails, times out, or the person is offline, what is already
 * here is a complete, buildable plan — which is why there is no error state.
 */
function PlanSteps({ cancelled }: { cancelled: { lost: string[] } | null }) {
  const navigate = useNavigate();
  const { isAuthenticated, user } = useSession();
  const store = useDraft();
  const idea = store.description;

  /*
   * A plan already in the draft is one this person has been looking at — back from signing
   * in, or from signing up. If the server has tailored this idea, that tailoring has been
   * paid for (/plan charges before it answers), so it is not bought again; with no working
   * copy in the draft it is what the screen opens on. Otherwise the tailoring still runs, but
   * lands on a resumed plan as a suggestion: it must never be laid over choices already made.
   */
  const [resumed] = useState(() => ({
    plan: store.plan != null,
    tailored: store.tailored?.idea === idea,
  }));
  const [plan, setPlan] = useState<AppPlan>(
    () => store.plan ?? (store.tailored?.idea === idea ? store.tailored.plan : scaffoldFor(idea)),
  );
  /** Whether /plan has finished — charged or refunded — so the balance read after it is true. */
  const [planSettled, setPlanSettled] = useState(resumed.tailored);
  /** Whether /plan has been out long enough that its charge is in the balance (see PLAN_CHARGE_TAKEN_MS). */
  const [planWaitOver, setPlanWaitOver] = useState(false);
  /**
   * The shortcut waits for tailoring, but not forever: a /plan that never answers would hold
   * "Looks good — build it" at "Tailoring your plan…" for good. After this, it builds the plan on
   * screen, which is what the steps would build too.
   */
  const [tailorGaveUp, setTailorGaveUp] = useState(false);
  useEffect(() => {
    const t = setTimeout(() => setTailorGaveUp(true), SHORTCUT_WAITS_MS);
    return () => clearTimeout(t);
  }, []);
  const shortcutReady = planSettled || tailorGaveUp;
  // Back on the step they left for sign-in, not at the start of the wizard.
  const [step, setStep] = useState(() => Math.max(0, STEPS.indexOf(store.planStep as StepId)));
  const [suggestion, setSuggestion] = useState<AppPlan | null>(null);
  const [refineNotice, setRefineNotice] = useState<string | null>(null);
  /** The notice is /plan's out-of-credits refusal, whose last sentence depends on the balance. */
  const [refusedForCredits, setRefusedForCredits] = useState(false);
  /**
   * /plan refused for want of a subscription. Its words depend on the offer, which may be read
   * after it (a trial or a subscription), so the refusal is kept and the sentence made on render.
   */
  const [tailoringRefused, setTailoringRefused] = useState(false);
  /**
   * What the checkout would sell this account: the trial, or — one per account — the plan from day
   * one. The trial until /credits says otherwise (absent is the trial: state/trial.ts).
   */
  const [offer, setOffer] = useState<Offer>('trial');
  /** A gift of free credits that has ended, said on the offer (gift-credits handoff). */
  const [giftEnded, setGiftEnded] = useState<'used_up' | 'expired' | null>(null);
  const [newFeature, setNewFeature] = useState('');
  /**
   * The long text somebody was told about as they tried to leave the content step without saying
   * where it came from — once; the next Next goes on (deep-dive F8: it used to be dropped silently).
   */
  const [bookWarned, setBookWarned] = useState<string | null>(null);
  const [matching, setMatching] = useState(false);
  /** Under the looks: what happened, and — when the session ended — the way back in. */
  const [styleNotice, setStyleNotice] = useState<{ text: string; signIn?: boolean } | null>(null);
  const [lookTools, setLookTools] = useState<LookTools>('unknown');
  const picture = useRef<HTMLInputElement>(null);
  // A user edit must never be overwritten by a refinement that was already in flight.
  const dirty = useRef(resumed.plan);

  const steps = STEPS;
  const current = steps[Math.min(step, steps.length - 1)];

  // Read once, above; left in the draft it would reopen a later visit mid-way too.
  useEffect(() => {
    if (useDraft.getState().planStep) useDraft.getState().setPlanStep(null);
  }, []);

  const edit = useCallback((next: (p: AppPlan) => AppPlan) => {
    dirty.current = true;
    setPlan(next);
  }, []);

  useEffect(() => {
    if (resumed.tailored) return;
    let live = true;
    const waited = setTimeout(() => {
      if (live) setPlanWaitOver(true);
    }, PLAN_CHARGE_TAKEN_MS);
    void (async () => {
      try {
        // Kept in the draft by tailorPlan itself, so leaving before it lands loses nothing.
        const { plan: refined } = await tailorPlan(idea);
        if (!live || !refined) return;
        if (dirty.current) setSuggestion(refined);
        else setPlan(refined);
      } catch (err) {
        if (!live) return;
        /*
         * The scaffold on screen is already a complete, buildable plan, so a failed
         * refinement is never fatal and never a redirect. But it is no longer FREE — /plan
         * charges — so silence would leave someone with a quietly worse plan and no idea
         * why. A dismissible line, and Build still works.
         */
        if (err instanceof ApiError && err.code === 'subscription_required') {
          /*
           * The server would not tailor this one without a subscription (infra/lambda/shared/
           * paywall.ts). Since 2026-09-29 it tailors a few plans a day for an account without one,
           * so this is no longer every new account — it is somebody who has used those, and was
           * shown a tailored plan before. Said silently, the plainer plan on screen had no reason
           * given; said as "out of credits", it named credits a new account never had. So: what a
           * tailored plan needs, and that this one still builds. No promise that it is tailored
           * after the checkout, because nothing tailors it then.
           */
          setTailoringRefused(true);
          // Whether a trial is still this account's to have is on /credits; asked now, not at the review.
          void creditBalance()
            .then((b) => {
              if (live) setOffer(offerFor(b));
            })
            .catch(() => undefined);
        } else if (err instanceof ApiError && err.kind === 'out_of_credits') {
          /*
           * No promise that the build is affordable: /plan is now refused whenever the balance
           * cannot cover the plan AND the build, so this can mean the build is out of reach too.
           * The price note under the plan says which, from the balance itself.
           */
          setRefineNotice("We couldn't tailor this plan — there aren't enough credits to tailor it and build it.");
          setRefusedForCredits(true);
        } else if (err instanceof ApiError && err.code === 'anon_plan_limit') {
          // A signed-out visitor past today's free tailored plans (webBuild.ts anonymousPlan).
          setRefineNotice("You've tailored a few plans today without an account. This one still builds — and with an account, we tailor every plan.");
        } else if (err instanceof ApiError && err.kind === 'unauthorized') {
          setRefineNotice('Sign in and we can tailor this plan to your idea.');
        }
        // Anything else stays silent: the person has a working plan and asked for nothing.
      } finally {
        clearTimeout(waited);
        if (live) setPlanSettled(true);
      }
    })();
    return () => {
      live = false;
      clearTimeout(waited);
    };
    // Runs once per idea: the refinement is of THIS idea, and re-running would fight edits.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [idea]);

  /**
   * The ranking on offer and how far through it we are — ONE value, deliberately.
   *
   * Held together rather than as two pieces of state, because on the phone they disagreed:
   * feedback set a new ranking while the round stayed where it was, and the model's best
   * answer was rotated out of view at the moment somebody asked for it.
   */
  const [kitChoice, setKitChoice] = useState<{ ranking: string[] | null; round: number }>(() => ({
    /*
     * Back from signing in, the look they picked may have come from "Show me others" or a
     * picture, and so not be among the plan's first three — and the effect below replaces a
     * selection that is not on offer with the first card. Put at the top, it is on offer.
     */
    ranking: resumed.plan ? withTopPick(plan.kitRanking, plan.kitId) : null,
    round: 0,
  }));
  const [kitFeedback, setKitFeedback] = useState('');
  const [rankingKits, setRankingKits] = useState(false);

  const shownKits = useMemo(
    () => kitOptions(kitChoice.ranking ?? plan.kitRanking, kitChoice.round),
    [kitChoice, plan.kitRanking],
  );

  // Whatever is on screen must be what gets built: the plan carries ONE kitId to the
  // generator, so a selection that scrolls out of the offered three would build a look the
  // person can no longer see.
  useEffect(() => {
    if (!shownKits.includes(plan.kitId)) edit((p) => ({ ...p, kitId: shownKits[0] }));
  }, [shownKits, plan.kitId, edit]);

  /*
   * The look step's two model calls go through /llm, which refuses a caller with no session.
   * The shared code turns every failure into "try again" — right for a network blip, and a
   * dead end for somebody who is not signed in, who can retry forever. So a signed-out
   * visitor is offered the sign-in instead of the calls (below), and a session that lapsed
   * mid-visit is caught here: the refusal is noted on the way through, and said as what it is.
   */
  function lookCall() {
    const refused: { sessionEnded: boolean; needs: 'plan' | 'topup' | null } = { sessionEnded: false, needs: null };
    const call: typeof generate = async (input) => {
      try {
        return await generate(input);
      } catch (err) {
        if (err instanceof ApiError && err.kind === 'unauthorized') refused.sessionEnded = true;
        /*
         * Refused for money, before the balance read said so. The shared sentence is "does not have
         * the credits", which a new account never had; the paywall's code says which way on it is
         * (infra/lambda/shared/paywall.ts): no subscription, or a month used up (F14).
         */
        if (err instanceof ApiError && err.kind === 'out_of_credits') {
          refused.needs = err.code === 'subscription_required' ? 'plan' : 'topup';
        }
        throw err;
      }
    };
    return { refused, generate: call };
  }

  /** What a failed look call is said as: the session, the plan, or the shared reason. */
  function lookFailure(refused: ReturnType<typeof lookCall>['refused'], reason: string): { text: string; signIn?: boolean } {
    if (refused.sessionEnded) return { text: SESSION_ENDED, signIn: true };
    if (refused.needs) {
      // Not offered again, to be refused again.
      setLookTools(refused.needs);
      return { text: lookToolsNeed(refused.needs, offer) };
    }
    return { text: reason };
  }

  /*
   * Whether the look tools are this account's (see LookTools), read when the look step opens. The
   * offer is read with it, so the line names the trial only to an account that can still have one.
   */
  const onLook = current === 'look';
  useEffect(() => {
    if (!onLook || !isAuthenticated || lookTools !== 'unknown') return;
    let live = true;
    void creditBalance()
      .then((b) => {
        if (!live) return;
        setOffer(offerFor(b));
        setLookTools(lookToolsFor(b));
      })
      .catch(() => {
        if (live) setLookTools('open');
      });
    return () => {
      live = false;
    };
  }, [onLook, isAuthenticated, lookTools]);

  /**
   * To sign in and come back to THIS plan, on this step: the plan (look included) and the
   * step are kept in the draft, and `from` rides through sign-up and confirmation too — the
   * invitation says "free account", so most who take it have none yet.
   */
  function signInForLooks(): void {
    void askForAccount('looks');
  }

  /**
   * The account, asked for where it is needed: at Build, at the photo step (uploads need one) and
   * for the look tools. The draft is put away first (draftStash.ts) and the step remembered, so
   * the sign-up, the emailed code and the sign-in bring them back to this step with this plan —
   * and from Build, straight on to the trial offer (`buildAfterSignUp`).
   */
  async function askForAccount(why: 'build' | 'photos' | 'looks'): Promise<void> {
    store.setPlan(plan);
    store.setAppName(plan.appName);
    store.setPlanStep(current);
    if (why === 'build') {
      store.setPendingIntent('build');
      rememberBuildAfterSignUp();
    }
    await stashDraft('').catch(() => undefined);
    navigate(why === 'looks' ? '/sign-in' : '/sign-up', { state: { from: '/new/plan', ...(why === 'build' ? { fromBuild: true } : {}) } });
  }


  async function applyKitFeedback(): Promise<void> {
    if (rankingKits) return;
    setRankingKits(true);
    setStyleNotice(null);
    try {
      const call = lookCall();
      const res = await rankKitsFromFeedback(idea, kitFeedback, isAuthenticated, { generate: call.generate });
      if (res.kind === 'failed') {
        setStyleNotice(lookFailure(call.refused, res.reason));
        return;
      }
      setKitChoice({ ranking: res.kitRanking, round: 0 });
      // The new best answer becomes the selected one: somebody who typed "warmer" has just
      // said the current choice is not what they want.
      edit((p) => ({ ...p, kitId: kitOptions(res.kitRanking, 0)[0] }));
      setKitFeedback('');
    } finally {
      setRankingKits(false);
    }
  }

  /*
   * The balance. An unanswered call leaves everything undefined, and `buildCostNote` stays
   * silent on undefined rather than guessing — a price we are not sure of is worse than no
   * price, because somebody acts on it.
   *
   * Read AFTER /plan has settled, never alongside it. Once an account has built, /plan
   * charges 500 before it answers, and a balance read in parallel could come back first:
   * 1,200 on screen, "you can afford it", 700 left and a refused build. Read again each
   * time the review step is reached, because the look step's own calls spend too, and a
   * number from before them is no longer the one the build will meet.
   */
  const [credits, setCredits] = useState<{
    tier?: 'free' | 'paid' | 'unlimited';
    creditsRemaining?: number;
  }>({});
  const onReview = current === 'review';

  /*
   * Back from the account that Build asked for: on to the trial offer, without a second press. Once
   * per Build (takeBuildAfterSignUp forgets it), and only signed in with a plan on screen.
   */
  const resumedBuild = useRef(false);
  useEffect(() => {
    if (!isAuthenticated || resumedBuild.current || !onReview) return;
    if (pressBuildOnArrival) {
      // Back from the "still waiting" email: the press it was sent for, not a new decision.
      pressBuildOnArrival = false;
      resumedBuild.current = true;
      void build(true);
      return;
    }
    if (!takeBuildAfterSignUp()) return;
    resumedBuild.current = true;
    store.setPendingIntent(null);
    void build(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated, onReview]);
  useEffect(() => {
    if (!onReview) {
      // Left the review, so what it said is about to be stale: back on it, the note stays
      // silent until the new read lands rather than showing the old number first.
      setCredits((c) => (Object.keys(c).length ? {} : c));
      return;
    }
    if (!isAuthenticated || !(planSettled || planWaitOver)) return;
    let live = true;
    void creditBalance()
      .then((b) => {
        if (!live) return;
        setCredits({
          tier: b.tier as 'free' | 'paid' | 'unlimited',
          creditsRemaining: b.creditsRemaining,
        });
      })
      .catch(() => undefined);
    return () => {
      live = false;
    };
  }, [isAuthenticated, planSettled, planWaitOver, onReview]);

  /*
   * Whether Build builds, or first takes the trial (the owner, 2026-09-25: the card is asked for
   * right before the build). Read as soon as the review is reached — NOT after /plan like the
   * balance, because it does not move with /plan's charge, and the offer has to be on screen
   * before anybody presses anything. 'unknown' says nothing: telling a subscriber "your card is
   * needed now" would be false, and so would silence about the card to somebody who needs one.
   */
  /*
   * 'pending': this tab came back from a trial checkout the account does not show yet (a webhook
   * slower than the success page waits). The trial is Stripe's already, so it is not offered again
   * — a second checkout would be a second trial — and Build goes back to wait for it.
   */
  const [access, setAccess] = useState<'unknown' | 'included' | 'trial' | 'pending'>('unknown');
  const ownerId = user?.id ?? '';
  useEffect(() => {
    if (!onReview || !isAuthenticated) return;
    let live = true;
    void creditBalance()
      .then((b) => {
        if (!live) return;
        // Asked whatever the answer, so a balance that shows the plan forgets the wait.
        const awaited = trialCheckoutAwaited(ownerId, b);
        setOffer(offerFor(b));
        setGiftEnded(b.gift?.ended ?? null);
        setAccess(buildsIncluded(b) ? 'included' : awaited ? 'pending' : 'trial');
      })
      .catch(() => undefined);
    return () => {
      live = false;
    };
  }, [onReview, isAuthenticated, ownerId]);

  /** Build is working out where to send them, or sending them. */
  const [checking, setChecking] = useState(false);
  /**
   * The panel before Stripe is showing (Paywall.tsx): Build found no plan on the account, and the
   * offer it needs is said, with what comes with it, before the next press leaves for checkout.
   */
  const [paywall, setPaywall] = useState(false);
  /** The app built before the card and held for the trial, when there is one (state/buildRun.ts). */
  const heldApp = useBuildRun((r) => r.heldApp);
  /** Being built behind the offer, and how far along (shell/BuildOverlay.tsx names the phases). */
  const buildingBehind = useBuildRun((r) => r.status === 'running' && r.behindOffer);
  const buildPhase = useBuildRun((r) => r.phase);
  useFunnelStep('offer_viewed_during_build', paywall && buildingBehind);

  /*
   * The funnel's step events (analytics/funnel.ts): the plan reached, each step as it is reached —
   * not while the panel before Stripe covers it — and the panel itself, with the offer it showed.
   */
  useFunnelStep('plan_viewed');
  useFunnelStep(`step_${current}_viewed` as FunnelStep, !paywall);
  // The journey log's names for the same two screens (preview-first handoff, 2026-10-04).
  useFunnelStep('capabilities_viewed', current === 'features' && !paywall);
  useFunnelStep('look_viewed', current === 'look' && !paywall);
  useFunnelStep('paywall_viewed', paywall, { offer });
  /**
   * Build pressed, and which way: the first step's shortcut or the review. Once per visit to the
   * step it was pressed on — a second press after a refusal is the same decision. The panel's own
   * press is not one: it is counted as the checkout it begins.
   */
  const pressedOn = useRef<StepId | null>(null);
  useEffect(() => {
    if (pressedOn.current !== current) pressedOn.current = null;
  }, [current]);
  function notePress(): void {
    if (paywall || pressedOn.current === current) return;
    pressedOn.current = current;
    const via: FunnelProps['via'] = current === 'features' ? 'shortcut' : 'review';
    funnelStep('build_pressed', { via });
  }
  const [payError, setPayError] = useState<{ text: string; signIn?: boolean } | null>(null);
  /**
   * What the stash said it could not keep, once it has been said. The press that finds it says it
   * and stops; the next press goes on to Stripe with the person knowing.
   */
  const [notKeptSaid, setNotKeptSaid] = useState<StashResult | null>(null);

  // A new step: what was said at Build was about the plan and attachments THEN, and they may change.
  useEffect(() => {
    setNotKeptSaid(null);
    setPayError(null);
  }, [current]);

  /*
   * The browser's Back on Stripe's page can restore this page from the back-forward cache exactly
   * as it was left — Build still spinning, so pressed for good. The draft is intact then (it is
   * the same page); only the button needs giving back.
   */
  useEffect(() => {
    const shown = (e: PageTransitionEvent): void => {
      if (e.persisted) setChecking(false);
    };
    window.addEventListener('pageshow', shown);
    return () => window.removeEventListener('pageshow', shown);
  }, []);

  /**
   * What this build will cost, worked out before the button rather than after it.
   *
   * This screen used to say nothing at all. An anonymous visitor walked six steps, pressed
   * "Build this app", and met a Create account screen — never told the price, never told an
   * account was needed. The sentence is shared with the phone so the two cannot drift.
   *
   * `firstBuildDone: true` whatever the server says: the owner removed the free first app
   * (2026-09-25), so no build is free and the shared note's "This one is free" must never be
   * reached from here. It stays silent until the balance is read, as before.
   */
  const costNote = buildCostNote({
    signedIn: isAuthenticated,
    tier: credits.tier,
    creditsRemaining: credits.creditsRemaining,
    firstBuildDone: true,
  });

  /**
   * `resumed`: the Build press that asked for the account, carried on after it (see the effect
   * above). Already counted as `build_pressed` before the detour, so not counted a second time.
   */
  /*
   * "Make one like this for you" on the ad's demo (trial14-and-demo handoff §2, 2026-10-12): straight
   * to building — this screen presses its own "Looks good — build it" the moment the plan is ready,
   * with the same default features and look that button builds. Once, and only for that press.
   */
  const autoBuildTaken = useRef(false);
  useEffect(() => {
    if (autoBuildTaken.current || current !== 'features' || !shortcutReady || checking) return;
    autoBuildTaken.current = true;
    if (takeAutoBuild()) void build();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [current, shortcutReady, checking]);

  async function build(resumed = false): Promise<void> {
    /*
     * The "Anything else" words go on the plan here, as the phone's layout step puts them there
     * (withAnythingElse, shared): on the arrangement when one was chosen, else as a feature of
     * their own with one fixed id — so pressing Build again replaces the line, never adds one.
     */
    const words = useDraft.getState().anythingElse;
    const final = withAnythingElse(withoutAnythingElse(plan, words), words);
    store.setPlan(final);
    store.setAppName(final.appName);
    /*
     * Counted before the sign-up detour: a signed-out ad visitor who pressed Build and never finished
     * signing up is the funnel's biggest drop, and it recorded no press at all (review, 2026-10-01).
     */
    if (!resumed) notePress();
    if (!isAuthenticated) {
      /*
       * Signed out, Build BUILDS (the owner's flow, 2026-10-08): under the browser's id, held — and
       * the account is asked for while the site renders, on the reveal screen, which shows the site
       * once it is made and the offer over it. Photos need an account to upload, so a plan with
       * files still asks first (askForAccount), as before.
       */
      const d = useDraft.getState();
      if (d.attachedMedia.length > 0 || !!d.attachedDoc || (d.attachedDocs?.length ?? 0) > 0) {
        await askForAccount('build');
        return;
      }
      store.setPlanStep(current);
      void useBuildRun.getState().start('', undefined, { behindOffer: true });
      navigate('/new/building');
      return;
    }
    if (checking) return;
    if (!resumed) notePress();
    setPayError(null);
    setChecking(true);
    /*
     * Asked again at the press, whatever the review step read: a trial started in another tab,
     * or a subscription that lapsed, is the truth now. A read that fails is not a "no" — that
     * would ask a subscriber for a card — nor a "yes", which the server would refuse. Said.
     */
    let balance: CreditBalance;
    try {
      balance = await creditBalance();
    } catch {
      setPayError({ text: PLAN_CHECK_FAILED });
      setChecking(false);
      return;
    }
    const awaited = trialCheckoutAwaited(ownerId, balance);
    if (buildsIncluded(balance)) {
      setAccess('included');
      setChecking(false);
      void useBuildRun.getState().start(ownerId);
      return;
    }
    if (awaited) {
      setAccess('pending');
      await waitForTrial(awaited);
      return;
    }
    /*
     * The offer is said BEFORE anybody is sent to Stripe, on the panel (Paywall.tsx), which the
     * first press opens and whose own button is the press that goes. A panel already showing other
     * terms than the account needs now — a trial used in another tab meanwhile — shows the new ones
     * and waits for the next press: nobody goes to a $20 charge under "7 days free".
     */
    const needed = offerFor(balance);
    setOffer(needed);
    setGiftEnded(balance.gift?.ended ?? null);
    setAccess('trial');
    /*
     * Built before the card (preview-first handoff v2, 2026-10-04): somebody the trial is for has
     * their app built first, and the panel then says it is ready. The server keeps it — no page, no
     * charge — until the trial pays for it under this same key; the return from Stripe asks again
     * and it is handed over. Asked again from here on a second visit, the same choices are the same
     * key and come back held at once; changed choices are a new build, which the server holds only
     * once a day per account — past that it refuses as before, and the panel offers the trial.
     */
    if (!paywall) {
      setChecking(false);
      /*
       * Built, shown, then offered (the owner's flow, 2026-10-08): the reveal screen shows progress,
       * then their own site for a moment, then the offer over it — for the trial or, after one, the
       * subscription. If the server holds no build today (one a day), that screen shows the offer.
       */
      // Back on this step if they cancel at Stripe, rather than at the start of the wizard.
      store.setPlanStep(current);
      void useBuildRun.getState().start(ownerId, undefined, { behindOffer: true });
      navigate('/new/building');
      return;
    }
    if (!paywall || offer !== needed) {
      setPaywall(true);
      setChecking(false);
      return;
    }
    await checkout(needed);
  }

  /*
   * paywall_left: the panel closed, or the page with it, without going to Stripe — and after how
   * long. The trip to Stripe is `checkout_started`, so it is not also a leave.
   */
  const paywallOpenedAt = useRef<number | null>(null);
  const toCheckout = useRef(false);
  useEffect(() => {
    if (!paywall) return;
    // For the "still waiting" email: the server notes it once, and only without a trial.
    void noteWaiting(plan.appName, idea);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paywall]);
  useEffect(() => {
    if (!paywall) return;
    paywallOpenedAt.current = Date.now();
    toCheckout.current = false;
    const onHide = (): void => {
      if (toCheckout.current || paywallOpenedAt.current === null) return;
      funnelStep('paywall_left', { offer, seconds: secondsBucket(Date.now() - paywallOpenedAt.current) }, { now: true });
      paywallOpenedAt.current = null;
    };
    window.addEventListener('pagehide', onHide);
    return () => window.removeEventListener('pagehide', onHide);
  }, [paywall, offer]);

  /** Off the panel, back to the step it was opened from, with nothing it said still standing. */
  /** Putting the offer away: asked once, ever, what stopped them (ExitQuestion), then closed. */
  const [askingExit, setAskingExit] = useState(false);
  function leaveOffer(): void {
    if (exitQuestionAsked()) closePaywall();
    else setAskingExit(true);
  }

  function closePaywall(): void {
    // Still building: it carries on as the pill, so going back to the plan does not lose it.
    if (useBuildRun.getState().status === 'running' && useBuildRun.getState().behindOffer) {
      useBuildRun.setState({ behindOffer: false, minimised: true });
    }
    if (paywallOpenedAt.current !== null) {
      funnelStep('paywall_left', { offer, seconds: secondsBucket(Date.now() - paywallOpenedAt.current) });
      paywallOpenedAt.current = null;
    }
    setPaywall(false);
    setNotKeptSaid(null);
    setPayError(null);
  }

  /**
   * Back to the success page to wait for a trial Stripe already has, and build this plan when the
   * account shows it — exactly where Stripe returns a trial taken here. The plan is put away first
   * because that page brings the plan back from there, as it does after the real trip; a file the
   * browser would not keep is asked about there, before any build.
   */
  async function waitForTrial(sessionId: string): Promise<void> {
    store.setPlanStep(current);
    await stashDraft(ownerId);
    navigate(`/billing/success?session_id=${encodeURIComponent(sessionId)}&resume=build`);
  }

  /**
   * To the trial checkout, with the draft put away first so it survives the trip, and the server
   * asked to send the browser back to START the build (the contract: `resume: 'build'`).
   */
  async function checkout(sold: Offer): Promise<void> {
    // Back on this step if they cancel, rather than at the start of the wizard.
    store.setPlanStep(current);
    const kept = await stashDraft(ownerId);
    if ((!kept.saved || kept.lost.length > 0) && !notKeptSaid) {
      setNotKeptSaid(kept);
      setChecking(false);
      return;
    }
    // Which offer this was, for the page Stripe sends them back to (state/trial.ts).
    rememberCheckoutOffer(sold);
    const result = await startCheckout(
      'subscription',
      (url) => {
        // Right before the browser goes, and only when it does: a checkout that could not even be
        // opened is not one begun. Once per draft (state/trial.ts).
        // Ours, sent at once and kept alive past the page; Meta's copy too, with consent (analytics/funnel.ts).
        toCheckout.current = true;
        funnelStep('checkout_started', { offer: sold }, { now: true });
        if (reportInitiateCheckout(ownerId, idea)) setTimeout(() => leaveFor(url), LEAVE_AFTER_EVENT_MS);
        else leaveFor(url);
      },
      { resume: 'build' },
    );
    // The page is leaving for Stripe; the button stays busy until it has gone.
    if (result.status === 'redirecting') return;
    setChecking(false);
    if (result.status === 'signin_required') {
      setPayError({ text: 'Your session has ended. Sign in again to start your trial — your plan is kept.', signIn: true });
      return;
    }
    if (result.reason === 'already_subscribed') {
      /*
       * The server knows of a subscription the read above did not show: the webhook landed in
       * between, or a trial began in another tab. Then this is a build, not a checkout.
       */
      const now = await creditBalance().catch(() => null);
      if (now && buildsIncluded(now)) {
        void clearDraftStash();
        setAccess('included');
        void useBuildRun.getState().start(ownerId);
        return;
      }
      setPayError({ text: 'You already have a subscription — it can take a minute to show here. Press it again in a moment.' });
      return;
    }
    setPayError({ text: result.message });
  }

  /**
   * Turn a reference picture into a look.
   *
   * The decisions are the shared ones — `pickStyleFromPicture` takes injectable `pick` and
   * `generate`, so the browser supplies a file input and its own proxy call, and everything
   * that JUDGES anything (parsing the reply, choosing the nearest kit, refusing a reply it
   * cannot read) stays identical to the phone's.
   *
   * A picture that gives no clear look leaves the plan EXACTLY as it was and says so. A
   * half-applied style — a kit that changed for no visible reason — is worse than none.
   */
  async function matchPicture(file: File): Promise<void> {
    setMatching(true);
    setStyleNotice(null);
    try {
      /*
       * Re-encoded first, by the composer's own encoder: a JPEG at most 2000px wide (F14). Read as
       * it came, a 3-8MB phone photo was 4-11MB of base64, past Lambda's 6MB request limit or the
       * model's image limit, and a HEIC the model may not take. One the browser cannot decode is
       * said by name, and the model is asked nothing.
       */
      const { added, problems } = await readPicks([file], { picked: [], existing: [] });
      const photo = added.find((p) => p.kind === 'photo');
      if (!photo || photo.kind !== 'photo') {
        setStyleNotice({ text: problems[0] ?? 'Could not read that picture. Save it as JPEG and choose it again.' });
        return;
      }
      const image = await readAsDataUri(photo.file);
      const call = lookCall();
      const result = await pickStyleFromPicture(idea, isAuthenticated, {
        pick: async () => image,
        generate: call.generate,
      });
      // Cancelling is not a failure and must not be reported as one.
      if (!result) return;
      if (result.kind === 'failed') {
        setStyleNotice(lookFailure(call.refused, result.reason));
        return;
      }
      /*
       * The nearest kit can be any of the eight, not only the three on screen, so it goes to
       * the FRONT of the ranking as well. Setting the kit alone let the effect above swap an
       * unoffered kit straight back to shownKits[0] — the notice below then named a look that
       * was neither ticked nor built. The same shared rule as the phone's.
       */
      setKitChoice((c) => ({ ranking: withTopPick(c.ranking ?? plan.kitRanking, result.kitId), round: 0 }));
      edit((p) => ({ ...p, kitId: result.kitId }));
      store.setStyleBrief(result.styleBrief);
      setStyleNotice({ text: `Using ${KITS.find((k) => k.id === result.kitId)?.label ?? result.kitId}, matched to your picture.` });
    } finally {
      setMatching(false);
      // So the same picture can be chosen again.
      if (picture.current) picture.current.value = '';
    }
  }

  /** Next, except off the content step with a long text that will not be built — said once. */
  const next = (): void => {
    const book = useDraft.getState().pendingBook;
    if (current === 'content' && book && !documentReady(book) && bookWarned !== book.fileName) {
      setBookWarned(book.fileName);
      return;
    }
    setBookWarned(null);
    setStep((s) => Math.min(s + 1, steps.length - 1));
  };

  const addFeature = (): void => {
    const label = newFeature.trim();
    if (!label) return;
    edit((p) => ({ ...p, features: [...p.features, userFeature(label, p.features)] }));
    setNewFeature('');
  };

  /*
   * What the last Build press stopped to say: a file checkout cannot carry, or why nothing
   * happened. Shown wherever that press was — the panel, the review, or the first step's shortcut.
   */
  const pressNotices = (
    <>
      {notKeptSaid && (
        <p role="status" className="type-caption plan-detail plan-notice">
          {notKeptSaid.saved
            ? notKept(notKeptSaid.lost)
            : "This browser couldn't keep your plan while you're at checkout, so you'd need to describe it again afterwards."}
        </p>
      )}
      {payError && (
        <p role="alert" className="type-caption field-error-text">
          {payError.text}
          {payError.signIn && (
            <>
              {' '}
              <button type="button" className="chip-button" onClick={signInForLooks}>Sign in</button>
            </>
          )}
        </p>
      )}
    </>
  );

  if (paywall) {
    return (
      <Screen title={TITLES.review}>
        {askingExit ? (
          <ExitQuestion onDone={() => { setAskingExit(false); closePaywall(); }} />
        ) : (
        <Paywall
          /*
           * The plan as it is NOW, with the "Anything else" words on it as Build puts them — not the
           * copy Build left in the draft at the press that opened this. The shortcut can be pressed
           * before /plan's tailoring lands; landing while the panel is open, it is what is shown, and
           * the panel's own press puts it in the draft that goes through checkout.
           */
          plan={withAnythingElse(withoutAnythingElse(plan, store.anythingElse), store.anythingElse)}
          offer={offer}
          ready={!!heldApp && offer === 'trial'}
          giftEnded={giftEnded}
          building={buildingBehind ? phaseLabel(buildPhase) : undefined}
          busy={checking}
          continueLabel={notKeptSaid ? 'Continue to checkout' : undefined}
          notice={pressNotices}
          onContinue={() => void build()}
          onBack={leaveOffer}
        />
        )}
      </Screen>
    );
  }

  return (
    <Screen title={TITLES[current]}>
      <ol className="plan-progress" aria-label="Progress">
        {steps.map((s, i) => (
          <li key={s} className={i <= step ? 'plan-dot is-done' : 'plan-dot'} aria-current={i === step ? 'step' : undefined}>
            <span className="visually-hidden">{TITLES[s]}</span>
          </li>
        ))}
      </ol>
      <p className="type-body plan-sub">{SUBTITLES[current]}</p>

      {cancelled && (
        <p role="status" className="type-caption plan-detail plan-notice">
          {checkoutCancelled(checkoutOffered())}
          {cancelled.lost.length > 0 && <> {notKept(cancelled.lost)}</>}
        </p>
      )}

      {(refineNotice || tailoringRefused) && (
        <p role="status" className="type-caption plan-detail plan-notice">
          {tailoringRefused ? tailoringNeeds(offer) : refineNotice}{' '}
          {/*
            "The plan below still works as it is" — said to a free account that had built its one
            app, while the note under the plan said the build needs credits they do not have. Said
            now only when the balance is KNOWN to cover the build: not when it is short, and not
            before it has been read (which is the review step), because that would be a guess.
          */}
          {refusedForCredits && (costNote.tone === 'cost' || access === 'trial') && (
            <>The plan below still works as it is.{' '}</>
          )}
          <button type="button" className="chip-button" onClick={() => { setRefineNotice(null); setRefusedForCredits(false); setTailoringRefused(false); }}>Dismiss</button>
        </p>
      )}

      {suggestion && (
        <div className="plan-suggestion card clay-card" role="status">
          <p className="type-body">We refined this plan while you were editing.</p>
          <Button
            title="Use the refined plan"
            variant="secondary"
            onPress={() => {
              setPlan(suggestion);
              // Its own ranking, not the one kept for the plan it replaces.
              setKitChoice({ ranking: null, round: 0 });
              setSuggestion(null);
            }}
          />
          <Button title="Keep mine" variant="ghost" onPress={() => setSuggestion(null)} />
        </div>
      )}

      {current === 'features' && (
        <>
          <ul className="plan-list">
            {plan.features.map((f) => (
              <li key={f.id} className="card clay-card plan-item">
                <label className="plan-check">
                  <input
                    type="checkbox"
                    checked={f.included}
                    onChange={() => {
                      // Whether it went on or off — never which feature (its label is the person's plan).
                      funnelStep('capability_toggled', { on: !f.included });
                      edit((p) => ({
                        ...p,
                        features: p.features.map((x) => (x.id === f.id ? { ...x, included: !x.included } : x)),
                      }));
                    }}
                  />
                  <span>
                    <span className="type-heading">{f.label}</span>
                    <span className="type-caption plan-detail">{f.detail}</span>
                  </span>
                </label>
              </li>
            ))}
          </ul>
          <div className="plan-add">
            <label className="type-caption field-label" htmlFor="new-feature">Add something missing</label>
            <input
              id="new-feature"
              className="field"
              value={newFeature}
              onChange={(e) => setNewFeature(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  addFeature();
                }
              }}
            />
            <Button title="Add" variant="secondary" onPress={addFeature} disabled={!newFeature.trim()} />
          </div>
        </>
      )}

      {current === 'content' && <ContentStep onNeedAccount={isAuthenticated ? undefined : () => void askForAccount('photos')} />}
      {current === 'content' && bookWarned && (
        <p role="alert" className="type-caption field-error-text">
          {bookWarned} isn’t attached yet — say where it came from, or remove it. Press Next again to go on
          without it.
        </p>
      )}

      {current === 'look' && (
        <>
          <label className="type-caption field-label" htmlFor="app-name">Name</label>
          <input
            id="app-name"
            className="field"
            value={plan.appName}
            onChange={(e) => edit((p) => ({ ...p, appName: e.target.value }))}
          />
          <label className="type-caption field-label" htmlFor="tagline">Tagline</label>
          <input
            id="tagline"
            className="field"
            value={plan.tagline}
            onChange={(e) => edit((p) => ({ ...p, tagline: e.target.value }))}
          />
          {/*
            * Three directions, each wearing this person's own app.
            *
            * The browser gets the kit's font stack exactly as written, so the mockup uses the
            * same faces the built page will. What is sketched is the arrangement, which the
            * generator decides; what is exact is the identity, which is what is being chosen.
            */}
          <div role="radiogroup" aria-label="How it looks">
            {shownKits.map((id) => {
              const kit = kitById(id);
              if (!kit) return null;
              const selected = plan.kitId === id;
              return (
                <button
                  key={id}
                  type="button"
                  role="radio"
                  aria-checked={selected}
                  className={`look-card pressable ${selected ? 'is-selected' : ''}`}
                  onClick={() => {
                    funnelStep('look_chosen', { index: String(Math.min(shownKits.indexOf(id), 7)) as NonNullable<FunnelProps['index']> });
                    edit((p) => ({ ...p, kitId: id }));
                  }}
                >
                  <span className="look-head">
                    <span className="type-heading">{kit.label}</span>
                    {selected && <span aria-hidden="true">✓</span>}
                  </span>
                  <span className="type-caption plan-detail">{kit.blurb}</span>
                  <span className="look-strip">
                    <KitMockup kit={kit} plan={plan} width={100} />
                  </span>
                </button>
              );
            })}
          </div>

          <input
            ref={picture}
            id="style-picture"
            type="file"
            accept="image/*"
            className="visually-hidden"
            onChange={(e) => {
              const file = e.target.files?.[0];
              if (file) void matchPicture(file);
            }}
          />
          <div className="look-actions">
            <Button
              title="Show me others"
              variant="secondary"
              onPress={() => setKitChoice((c) => ({ ...c, round: c.round + 1 }))}
              accessibilityLabel="Show me three other looks"
            />
            {isAuthenticated && (lookTools === 'unknown' || lookTools === 'open') && (
              <Button
                title={matching ? 'Reading…' : 'Match a picture'}
                variant="secondary"
                loading={matching}
                onPress={() => picture.current?.click()}
                accessibilityLabel="Choose the look from a picture"
              />
            )}
          </div>

          {/* Say what you want instead. Kits are a closed set the compile gate enforces, so
              this re-ranks the eight rather than inventing a ninth. Both this and the
              picture are model calls, which need an account: signed out, the invitation
              stands where they would, and the three kits and "Show me others" still work. */}
          {!isAuthenticated ? (
            <p className="type-caption plan-detail">
              {SIGN_IN_FOR_LOOKS}{' '}
              <button type="button" className="chip-button" onClick={signInForLooks}>Sign in</button>
            </p>
          ) : lookTools === 'plan' || lookTools === 'topup' ? (
            // Said once: a refusal that closed them has already said it, under the looks.
            !styleNotice && <p className="type-caption plan-detail">{lookToolsNeed(lookTools, offer)}</p>
          ) : (
            <div className="look-feedback">
              <input
                className="field"
                value={kitFeedback}
                onChange={(e) => setKitFeedback(e.target.value)}
                placeholder="Warmer? Bolder? Less corporate?"
                disabled={rankingKits}
                aria-label="Say what you would like the look to be"
                onKeyDown={(e) => { if (e.key === 'Enter') void applyKitFeedback(); }}
              />
              <Button
                title={rankingKits ? '…' : 'Go'}
                loading={rankingKits}
                disabled={!kitFeedback.trim()}
                onPress={() => void applyKitFeedback()}
                accessibilityLabel="Show looks matching that"
              />
            </div>
          )}

          {styleNotice && (
            <p role="status" className="type-caption plan-detail">
              {styleNotice.text}
              {styleNotice.signIn && (
                <>
                  {' '}
                  <button type="button" className="chip-button" onClick={signInForLooks}>Sign in</button>
                </>
              )}
            </p>
          )}
        </>
      )}

      {current === 'charts' && (
        <ul className="plan-list">
          {plan.charts.length === 0 && <li className="type-body">No charts for this one.</li>}
          {plan.charts.map((c) => (
            <li key={c.id} className="card clay-card plan-item">
              <label className="plan-check">
                <input
                  type="checkbox"
                  checked={c.included}
                  onChange={() =>
                    edit((p) => ({
                      ...p,
                      charts: p.charts.map((x) => (x.id === c.id ? { ...x, included: !x.included } : x)),
                    }))
                  }
                />
                <span>
                  <span className="type-heading">{c.label}</span>
                  <span className="type-caption plan-detail">{c.source}</span>
                </span>
              </label>
            </li>
          ))}
        </ul>
      )}

      {current === 'review' && (
        <div className="plan-review">
          <h2 className="type-title">{plan.appName}</h2>
          <p className="type-body plan-detail">{plan.tagline}</p>

          {/* The journey, before the inventory: "is this the app I asked for?" is a question
              about what using it feels like, not about which rows exist. */}
          <h3 className="type-micro section-label">What someone goes through</h3>
          <JourneyDiagram plan={plan} />

          <h3 className="type-micro section-label">It will</h3>
          <ul className="plan-review-list">
            {plan.features.filter((f) => f.included).map((f) => (
              <li key={f.id} className="type-body">{f.label}</li>
            ))}
          </ul>
          {plan.constraints.length > 0 && (
            <>
              <h3 className="type-micro section-label">Worth knowing</h3>
              <ul className="plan-review-list">
                {plan.constraints.map((c) => (
                  <li key={c.id} className="type-body">
                    {c.label}
                    {c.workaround && <span className="type-caption plan-detail">{c.workaround}</span>}
                  </li>
                ))}
              </ul>
            </>
          )}
          <AttachmentsReview />
          {/*
            The phone's "Anything else" box, which is where both demos said "open with our photo
            full width at the top" (deep-dive F11). Put on the plan at Build.
          */}
          <label className="type-caption field-label" htmlFor="anything-else">Anything else?</label>
          <textarea
            id="anything-else"
            className="field review-anything-else"
            rows={2}
            maxLength={500}
            value={store.anythingElse}
            placeholder="Open with our storefront photo, full width at the top."
            onChange={(e) => store.setAnythingElse(e.target.value)}
          />
          {/* The price, before the button. This screen said nothing at all: an anonymous
              visitor walked six steps, pressed Build, and met a Create account screen —
              never told the cost, never told an account was needed. */}
          {/* For somebody without a plan, the offer — in the owner's words, before the button,
              and before anybody is sent to Stripe. A price in credits means nothing to them.
              Signed out too (the journey is guarded, so only a lapsed session lands here): the
              shared note would say "Your first app is free", which is true for nobody now. */}
          {(access === 'trial' || !isAuthenticated) && (
            <div className="cost-note tone-invite" role="note">
              <span className="type-body cost-headline">{offerTerms(offer).headline}</span>{' '}
              <span className="type-caption cost-detail">{offerTerms(offer).detail}</span>
            </div>
          )}
          {/* Back from Stripe before the account caught up: the card was given, so it is not
              asked for again, and the price in credits would read "you have 0" — the free tier
              the account still shows — so neither note is drawn. */}
          {access === 'pending' && isAuthenticated && (
            <div className="cost-note tone-invite" role="note">
              <span className="type-body cost-headline">{awaitedHeadline(checkoutOffered() ?? 'trial')}</span>{' '}
              <span className="type-caption cost-detail">{TRIAL_AWAITED_DETAIL}</span>
            </div>
          )}
          {access !== 'trial' && access !== 'pending' && isAuthenticated && costNote.tone !== 'silent' && (
            <div className={`cost-note tone-${costNote.tone}`} role="note">
              <span className="type-body cost-headline">{costNote.headline}</span>
              <span className="type-caption cost-detail">{costNote.detail}</span>
              {/* "Top up to build it", with nowhere on the screen to top up. The way past the
                  note is one tap away, from the note itself. A subscriber is short and tops up;
                  somebody with no plan is told to subscribe, and /credits is where both are
                  sold — it offers Subscribe to anybody a store is not billing. */}
              {costNote.tone === 'short' && (
                <Link to="/credits" className="type-caption cost-link">Get credits</Link>
              )}
              {costNote.tone === 'subscribe' && (
                <Link to="/credits" className="type-caption cost-link">Get Saddle Pro</Link>
              )}
            </div>
          )}
        </div>
      )}

      {pressNotices}

      {current === 'features' ? (
        /*
         * The shortcut (the owner, 2026-10-01). Four steps stood between a plan somebody liked and
         * the build, each with its own Next, on a phone, from an ad. The plan on screen is complete
         * and buildable as it is — the look is the one the look step would open on ticked (the effect
         * above keeps plan.kitId among the three offered), content is optional — so the first step
         * offers to build it. Photos are named on their own, so somebody who came to put their
         * pictures on a page is not rushed past the step where they go; both secondaries lead into
         * the same walk through the steps, which is every path there was before.
         */
        <div className="plan-actions plan-actions-stack">
          {/*
            * Not while /plan's tailoring is still on its way (10-40s): a subscriber's press built the
            * untailored scaffold and the tailored plan landed after the build had started (review of
            * lane/journey-polish, 2026-10-01). It says what it is waiting for instead.
            */}
          <Button
            title={shortcutReady ? 'Looks good — build it' : 'Tailoring your plan…'}
            onPress={() => void build()}
            loading={checking || !shortcutReady}
            disabled={!shortcutReady}
          />
          <Button title="Add your photos" variant="secondary" onPress={next} disabled={checking} />
          <Button title="Customize photos, look and details" variant="ghost" onPress={next} disabled={checking} />
        </div>
      ) : (
        <div className="plan-actions">
          {step > 0 && <Button title="Back" variant="ghost" onPress={() => { setBookWarned(null); setStep((s) => s - 1); }} />}
          {current === 'review' ? (
            /*
             * "Build it" for everybody. For somebody without a plan the press opens the panel that
             * names the offer and its button (Paywall.tsx); the terms are already in the note above,
             * so this button does not have to promise them twice.
             */
            <Button title="Build it" onPress={() => void build()} loading={checking} accessibilityLabel="Build this app" />
          ) : (
            <Button title="Next" onPress={next} />
          )}
        </div>
      )}
    </Screen>
  );
}
